import { useCallback, useEffect, useRef, useState } from 'react';
import { BASE_URL } from './conf/config';

// Poll every minute; while a maintenance notice is active, every 20 seconds,
// so the countdown stays close and the switch to the maintenance page is quick.
const NORMAL_POLL_MS = 60000;
const MAINTENANCE_POLL_MS = 20000;

// Loads the user's messages from /api/messages/ and keeps them fresh.
export function useMessages(fetchWithAuth, enabled) {
    const [messages, setMessages] = useState([]);
    // server clock minus browser clock, so countdowns don't depend on the PC's time
    const [clockOffset, setClockOffset] = useState(0);

    // fetchWithAuth is a new function on every render; keep the latest in a ref
    // so the polling loop doesn't restart each time
    const fetchRef = useRef(fetchWithAuth);
    fetchRef.current = fetchWithAuth;

    // Returns true while a maintenance notice is active
    const load = useCallback(async () => {
        try {
            const res = await fetchRef.current(`${BASE_URL}/api/messages/`, { method: 'GET' });
            // nginx answers 503 while maintenance mode is on: reload so this tab shows that page
            if (res.status === 503) {
                window.location.reload();
                return false;
            }
            if (!res.ok) return false;
            const data = await res.json();
            const list = data.messages || [];
            setMessages(list);
            if (data.server_time) setClockOffset(Date.parse(data.server_time) - Date.now());
            return list.some((m) => m.kind === 'maintenance');
        } catch (error) {
            // network hiccup or logged out: keep the current list, try again next round
            return false;
        }
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        let timer = null;
        const tick = async () => {
            const maintenanceActive = await load();
            if (!cancelled) {
                timer = setTimeout(tick, maintenanceActive ? MAINTENANCE_POLL_MS : NORMAL_POLL_MS);
            }
        };
        tick();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [enabled, load]);

    const markRead = useCallback(async (ids) => {
        if (!ids.length) return;
        setMessages((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, read: true } : m)));
        try {
            await fetchRef.current(`${BASE_URL}/api/messages/read/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids }),
            });
        } catch (error) {
            // the next poll brings back the real read state
        }
    }, []);

    const now = useCallback(() => Date.now() + clockOffset, [clockOffset]);
    const unreadCount = messages.filter((m) => !m.read).length;

    return { messages, unreadCount, markRead, now };
}
